import locale from "@bonliva-crm/locale";
import {
  Box,
  CircularProgress,
  InputLabel,
  Typography,
  makeStyles
} from "@material-ui/core";
import ReactSelect, { GroupBase, OptionsOrGroups, Props } from "react-select5";
import { GroupHeadingComponent } from "./components/group-heading";
import { PlaceholderComponent } from "./components/placeholder";
import { useSelectStyle } from "./hooks/use-select-style";
import { DropdownIndicatorComponent } from "./components/dropdown-indicator";

type SelectProps<Option, Group extends GroupBase<Option> = GroupBase<Option>> = Props<
  Option,
  false,
  Group
> & {
  inputLabel?: string;
  onOptionEdit?: (option: Option) => void;
  onOptionDelete?: (option: Option) => void;
  styleProps?: Parameters<typeof useSelectStyle<Option, false, Group>>[0];
  labelShrink?: boolean;
};

export function SingleGroupedSelect<
  Option,
  Group extends GroupBase<Option> = GroupBase<Option>
>(mainProps: SelectProps<Option, Group>) {
  const { selectStyle, selectTheme } = useSelectStyle<Option, false, Group>(
    mainProps.styleProps
  );
  const classes = useStyles();

  const { inputLabel } = mainProps;

  const options = [...(mainProps.options ?? [])] as OptionsOrGroups<Option, Group>;

  return (
    <Box className={classes.root}>
      {inputLabel && (
        <InputLabel shrink={mainProps.labelShrink}>
          <Typography variant="body1" style={{ opacity: mainProps.isDisabled ? 0.5 : 1 }}>
            {inputLabel}
          </Typography>
        </InputLabel>
      )}
      <ReactSelect<Option, false, Group>
        options={options}
        components={{
          GroupHeading: (p) => GroupHeadingComponent({ ...p, textVariant: "h5" }),
          Placeholder: (p) => PlaceholderComponent(p),
          DropdownIndicator: (p) => DropdownIndicatorComponent(p),
          IndicatorSeparator: null,
          ClearIndicator: () => (mainProps.isClearable ? undefined : <></>)
        }}
        styles={selectStyle}
        theme={(theme) => ({
          ...theme,
          ...selectTheme
        })}
        closeMenuOnSelect={true}
        hideSelectedOptions={false}
        backspaceRemovesValue={true}
        noOptionsMessage={() => locale.NoMoreSearchResults}
        loadingMessage={() => <CircularProgress size={40} />}
        isMulti={false}
        isSearchable={true}
        {...mainProps}
      />
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column"
  }
}));

export default SingleGroupedSelect;
